import { experience, ExtraParamsKey } from '@logto/schemas';
import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { isNewOttFeatureEnabled } from '@/constants/env';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import { useCustomRegistrationConfig } from '@/pages/CustomRegister/hooks/use-custom-registration-config';

import MagicLinkGate from './MagicLinkGate.js';
import OidcOneTimeToken from './OidcOneTimeToken.js';
import { isNewOttGateFlow } from './is-new-ott-gate-flow.js';

type OneTimeTokenErrorState = {
  readonly errorCode?: string;
  readonly errorMessage?: string;
  readonly email?: string;
};

/**
 * Entry for `/one-time-token`:
 * - classic `source` (`password_reset_link`, `user_provisioning`, `password_reset_email`) → set password.
 * - FF on and no `source` → DXM validate/redirect gate (no extra query params).
 *
 * Renew allowlist only controls the error-page button.
 */
const OneTimeToken = () => {
  const [params] = useSearchParams();
  const navigate = useNavigateWithPreservedSearchParams();
  const config = useCustomRegistrationConfig();
  const isFeatureEnabled = config?.newOttFeatureEnabled ?? isNewOttFeatureEnabled;
  const isGateFlow = isNewOttGateFlow(params, isFeatureEnabled);

  const goToErrorPage = useCallback(
    (state?: OneTimeTokenErrorState) => {
      const next = new URLSearchParams(params);
      // Keep error fields in the query so renew UI survives refresh (state alone does not).
      if (state?.errorCode) {
        next.set('errorCode', state.errorCode);
      }
      if (state?.errorMessage) {
        next.set('errorMessage', state.errorMessage);
      }
      if (state?.email) {
        next.set(ExtraParamsKey.LoginHint, state.email);
      }
      navigate(
        {
          pathname: `/${experience.routes.oneTimeToken}/error`,
          search: next.size > 0 ? `?${next.toString()}` : '',
        },
        { replace: true, state }
      );
    },
    [navigate, params]
  );

  const errorCode = params.get('errorCode') ?? undefined;
  const errorMessage = params.get('errorMessage') ?? undefined;
  const hasUrlError = Boolean(errorCode ?? errorMessage);
  const loginHint = params.get(ExtraParamsKey.LoginHint);

  useEffect(() => {
    if (!hasUrlError) {
      return;
    }

    goToErrorPage({
      errorCode,
      errorMessage,
      email: loginHint ?? undefined,
    });
  }, [errorCode, errorMessage, goToErrorPage, hasUrlError, loginHint]);

  if (hasUrlError) {
    return null;
  }

  if (isGateFlow) {
    return <MagicLinkGate />;
  }

  return <OidcOneTimeToken />;
};

export default OneTimeToken;
